import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BillingApi,
  type BillingCycle,
  type BillingPortalFlow,
  ChangeWorkspacePlan,
  CheckoutWorkspacePlan,
  type PreviewPlanChangeResponseDto,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { toast } from 'sonner';

import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { redirectToStripe } from '@/core/billing/redirect-to-stripe';
import { useInvalidateWorkspaceEntitlements } from '@/core/hooks/use-entitlements';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { getServerErrorMessage } from '@/core/utils/server-error';

function toChangePlan(
  plan: WorkspacePlan | ChangeWorkspacePlan,
): ChangeWorkspacePlan | null {
  if (plan === WorkspacePlan.FREE || plan === ChangeWorkspacePlan.FREE) {
    return ChangeWorkspacePlan.FREE;
  }
  if (plan === WorkspacePlan.PLUS || plan === ChangeWorkspacePlan.PLUS) {
    return ChangeWorkspacePlan.PLUS;
  }
  if (plan === WorkspacePlan.TEAMS || plan === ChangeWorkspacePlan.TEAMS) {
    return ChangeWorkspacePlan.TEAMS;
  }
  return null;
}

function toCheckoutPlan(
  plan: WorkspacePlan | CheckoutWorkspacePlan | ChangeWorkspacePlan,
): CheckoutWorkspacePlan | null {
  if (
    plan === WorkspacePlan.PLUS ||
    plan === CheckoutWorkspacePlan.PLUS ||
    plan === ChangeWorkspacePlan.PLUS
  ) {
    return CheckoutWorkspacePlan.PLUS;
  }
  if (
    plan === WorkspacePlan.TEAMS ||
    plan === CheckoutWorkspacePlan.TEAMS ||
    plan === ChangeWorkspacePlan.TEAMS
  ) {
    return CheckoutWorkspacePlan.TEAMS;
  }
  return null;
}

function getBillingMutationError(
  error: unknown,
  billingDict: {
    checkoutError: string;
    planChangeError: string;
    subscriptionAlreadyActive: string;
    subscriptionNotActive: string;
    downgradeMembersConflict: string;
    downgradeReputationLocationsConflict: string;
  },
  fallbackKey: 'checkoutError' | 'planChangeError' = 'checkoutError',
): string {
  const codeMap: Record<string, string | undefined> = {
    subscriptionAlreadyActive: billingDict.subscriptionAlreadyActive,
    subscriptionNotActive: billingDict.subscriptionNotActive,
    downgradeMembersConflict: billingDict.downgradeMembersConflict,
    downgradeReputationLocationsConflict:
      billingDict.downgradeReputationLocationsConflict,
  };

  return getServerErrorMessage(error, {
    codeMap,
    fallbackMessage: billingDict[fallbackKey],
  });
}

export function useStartCheckout() {
  const dict = useDictionary();
  const billingDict = dict.workspace.billing;
  const workspace = useCurrentWorkspace();

  return useMutation({
    mutationFn: async ({
      plan,
      billingCycle,
      workspaceId,
    }: {
      plan: WorkspacePlan | CheckoutWorkspacePlan;
      billingCycle: BillingCycle;
      workspaceId?: string;
    }) => {
      if (!canPurchaseBillingInApp()) {
        throw new Error('Billing checkout is web-only on native');
      }
      const wsId = workspaceId ?? workspace?.id;
      if (!wsId) throw new Error('No workspace selected');

      const checkoutPlan = toCheckoutPlan(plan);
      if (!checkoutPlan) {
        throw new Error('Plan is not available for checkout');
      }

      const response = await BillingApi.createCheckoutSession({
        path: { workspaceId: wsId },
        body: {
          plan: checkoutPlan,
          billingCycle,
        },
      });

      const url = response.data?.url;
      if (!url) throw new Error('Missing checkout session URL');
      return url;
    },
    onSuccess: async (url) => {
      await redirectToStripe(url);
    },
    onError: (error) => {
      toast.error(
        canPurchaseBillingInApp()
          ? getBillingMutationError(error, billingDict)
          : billingDict.nativeWebOnlyNotice,
      );
    },
  });
}

export function useChangeSubscriptionPlan() {
  const dict = useDictionary();
  const billingDict = dict.workspace.billing;
  const workspace = useCurrentWorkspace();
  const invalidateEntitlements = useInvalidateWorkspaceEntitlements();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plan,
      billingCycle,
      workspaceId,
    }: {
      plan: WorkspacePlan | ChangeWorkspacePlan;
      billingCycle: BillingCycle;
      workspaceId?: string;
    }) => {
      const wsId = workspaceId ?? workspace?.id;
      if (!wsId) throw new Error('No workspace selected');

      const changePlan = toChangePlan(plan);
      if (!changePlan) {
        throw new Error('Plan is not available for plan change');
      }

      const response = await BillingApi.changeSubscriptionPlan({
        path: { workspaceId: wsId },
        body: {
          plan: changePlan,
          billingCycle,
        },
      });

      return response.data;
    },
    onSuccess: (_data, variables) => {
      const wsId = variables.workspaceId ?? workspace?.id;
      if (wsId) {
        invalidateEntitlements(wsId);
      }
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success(billingDict.planChangeSuccess);
    },
    onError: (error) => {
      toast.error(
        getBillingMutationError(error, billingDict, 'planChangeError'),
      );
    },
  });
}

export function usePreviewPlanChange() {
  const workspace = useCurrentWorkspace();

  return useMutation({
    mutationFn: async ({
      plan,
      billingCycle,
      workspaceId,
    }: {
      plan: WorkspacePlan | ChangeWorkspacePlan;
      billingCycle: BillingCycle;
      workspaceId?: string;
    }): Promise<PreviewPlanChangeResponseDto> => {
      const wsId = workspaceId ?? workspace?.id;
      if (!wsId) throw new Error('No workspace selected');

      const changePlan = toChangePlan(plan);
      if (!changePlan) {
        throw new Error('Plan is not available for plan change');
      }

      const response = await BillingApi.previewPlanChange({
        path: { workspaceId: wsId },
        body: {
          plan: changePlan,
          billingCycle,
        },
      });

      if (!response.data) {
        throw new Error('Missing plan change preview');
      }
      return response.data;
    },
  });
}

export function useCancelScheduledPlanChange() {
  const dict = useDictionary();
  const billingDict = dict.workspace.billing;
  const workspace = useCurrentWorkspace();
  const invalidateEntitlements = useInvalidateWorkspaceEntitlements();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workspaceId?: string) => {
      const wsId = workspaceId ?? workspace?.id;
      if (!wsId) throw new Error('No workspace selected');

      await BillingApi.cancelScheduledPlanChange({
        path: { workspaceId: wsId },
      });
    },
    onSuccess: (_data, workspaceId) => {
      const wsId = workspaceId ?? workspace?.id;
      if (wsId) {
        invalidateEntitlements(wsId);
      }
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success(billingDict.scheduledChange.undoSuccess);
    },
    onError: () => {
      toast.error(billingDict.scheduledChange.undoError);
    },
  });
}

export function useOpenPortal() {
  const dict = useDictionary();
  const billingDict = dict.workspace.billing;
  const workspace = useCurrentWorkspace();

  return useMutation({
    /**
     * `flow` deep-links the Stripe portal (e.g. straight to adding a card);
     * omitted, the portal opens on its home page.
     */
    mutationFn: async (variables?: {
      workspaceId?: string;
      flow?: BillingPortalFlow;
    }) => {
      if (!canPurchaseBillingInApp()) {
        throw new Error('Billing portal is web-only on native');
      }
      const wsId = variables?.workspaceId ?? workspace?.id;
      if (!wsId) throw new Error('No workspace selected');

      const response = await BillingApi.createPortalSession({
        path: { workspaceId: wsId },
        body: variables?.flow ? { flow: variables.flow } : {},
      });

      const url = response.data?.url;
      if (!url) throw new Error('Missing portal session URL');
      return url;
    },
    onSuccess: async (url) => {
      await redirectToStripe(url);
    },
    onError: () => {
      toast.error(
        canPurchaseBillingInApp()
          ? billingDict.portalError
          : billingDict.nativeWebOnlyNotice,
      );
    },
  });
}
